import { ESdChangeReason, IWFStep, IWorkFlow } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import {
  MiAggregateStrategy,
  readMiAggregation,
  writeMiAggregationAggregateTarget,
  writeMiAggregationChildKey,
  writeMiAggregationCollectAllTarget,
  writeMiAggregationStrategy
} from "../wf-utils/multi-instance-utils";

type Props = { step: IWFStep };
type State = {
  childOutputKey?: string;
  strategy?: MiAggregateStrategy;
  aggregateTarget?: string;
  collectAllTarget?: string;
};

class AggregationPropertiesBase extends PureSdComponent<Props & IWithSDProps & IWithContextProp, State> {
  constructor(p: any) {
    super(p);
    this.state = {
      childOutputKey: undefined,
      strategy: undefined,
      aggregateTarget: undefined,
      collectAllTarget: undefined
    };
  }

  private suggestName(childKey: string, suffix: "Agg" | "All") {
    const stepId = this.props.step?.id ?? "Step";
    return `MI_${childKey}_${stepId}_${suffix}`;
  }

  private getOutputKeys(): string[] {
    const step = this.props.step as any;
    const wf: IWorkFlow | undefined = (this.props as any).sd?.selectedWf;
    const outputs = (step?.outputParameters ?? []) as Array<{ id: string }>;
    const wfParams = wf?.parameters ?? [];
    return outputs.map((op) => wfParams.find((wp) => wp.id === op.id)?.name).filter((n): n is string => !!n);
  }

  async componentDidMount() {
    const sd: any = (this.props as any).sd;
    const step = this.props.step;
    const agg = readMiAggregation(step);

    const childOutputKey = agg.childOutputKey;
    const strategy = agg.strategy;

    let collectAllTarget = agg.collectAllTarget;
    if (childOutputKey) {
      const wantedAll = this.suggestName(childOutputKey, "All");
      if (collectAllTarget !== wantedAll) {
        collectAllTarget = wantedAll;
        await writeMiAggregationCollectAllTarget(sd, step, wantedAll, ESdChangeReason.WFStepMappingChange);
      }
    }

    let aggregateTarget = agg.aggregateTarget;
    if (childOutputKey) {
      const wantedAgg = this.suggestName(childOutputKey, "Agg");
      if (aggregateTarget !== wantedAgg) {
        aggregateTarget = wantedAgg;
        await writeMiAggregationAggregateTarget(sd, step, wantedAgg, ESdChangeReason.WFStepMappingChange);
      }
    }

    this.setState({ childOutputKey, strategy, aggregateTarget, collectAllTarget });
  }

  private async onChildKeyChange(childKey?: string) {
    const sd: any = (this.props as any).sd;
    const step = this.props.step;
    this.setState({ childOutputKey: childKey });

    if (!childKey) {
      return;
    }

    await writeMiAggregationChildKey(sd, step, childKey, ESdChangeReason.WFStepMappingChange);

    const allTarget = this.suggestName(childKey, "All");
    this.setState({ collectAllTarget: allTarget });
    await writeMiAggregationCollectAllTarget(sd, step, allTarget, ESdChangeReason.WFStepMappingChange);

    const aggTarget = this.suggestName(childKey, "Agg");
    this.setState({ aggregateTarget: aggTarget });
    await writeMiAggregationAggregateTarget(sd, step, aggTarget, ESdChangeReason.WFStepMappingChange);
  }

  private async onStrategyChange(strategy?: MiAggregateStrategy) {
    const sd: any = (this.props as any).sd;
    const step = this.props.step;

    this.setState({ strategy });

    if (strategy) {
      await writeMiAggregationStrategy(sd, step, strategy, ESdChangeReason.WFStepMappingChange);
      if (this.state.childOutputKey && !this.state.aggregateTarget) {
        const aggTarget = this.suggestName(this.state.childOutputKey, "Agg");
        this.setState({ aggregateTarget: aggTarget });
        await writeMiAggregationAggregateTarget(sd, step, aggTarget, ESdChangeReason.WFStepMappingChange);
      }
    }
  }

  render() {
    const outputKeys = this.getOutputKeys();
    const { childOutputKey, strategy } = this.state;

    return (
      <section className="kuika-wf-step__section" data-testid="mi-aggregation">
        <div className="kuika-wf-step__section-head">
          <KuikaSvg
            name="arrows-horizontal"
            width={14}
            height={14}
            color="var(--dark-9)"
            className="kuika-wf-step__section-icon"
          />
          <span className="kuika-wf-step__section-title">Multi-Instance Aggregation</span>
        </div>
        <p className="kuika-wf-step__hint">Select child output and strategy.</p>

        <div>
          <span className="kuika-wf-step__form-label">Child Output Key</span>
          <KuikaSelectbox
            className="kuika-wf-step__select"
            size="sm"
            placeholder="Select one output"
            value={childOutputKey}
            onChange={(v) => this.onChildKeyChange(v)}
            options={outputKeys.map((k: string): KuikaSelectboxOption => ({ value: k, label: k }))}
          />
        </div>

        <div>
          <span className="kuika-wf-step__form-label">Strategy</span>
          <KuikaSelectbox
            className="kuika-wf-step__select"
            size="sm"
            placeholder="Select strategy"
            value={strategy}
            onChange={(v) => this.onStrategyChange(v as MiAggregateStrategy)}
            options={["Majority", "SumInt", "SumDouble", "AverageDouble", "Min", "Max"].map(
              (s: string): KuikaSelectboxOption => ({
                value: s,
                label: s
              })
            )}
          />
        </div>
      </section>
    );
  }

  checkNeedRender = (): boolean => true;
}

const AggregationPropertiesHOC = withContext<Props>(withSD<Props>(AggregationPropertiesBase));
export const AggregationProperties = AggregationPropertiesHOC as React.ComponentType<Props>;
